import { Helmet } from 'react-helmet-async';
import {
  GaugeCircle, LayoutGrid, Users, Scaling, Activity,
  Percent, Wallet, LifeBuoy, Trophy, TrendingUp,
  MonitorSmartphone, Filter, Headset, BarChart3, BookOpenCheck,
  ArrowRight, Info
} from 'lucide-react';
import { Button, Eyebrow, Reveal, Section, SectionHeader, PageBanner, CTAStrip } from '../components/ui';

export default function ParaCorretoras() {
  return (
    <main className="page-enter">
      <Helmet>
        <title>Para Corretoras | MedAliados — Benefícios, Comissões e Diferenciais</title>
        <meta name="description" content="Descubra como corretoras parceiras da MedAliados aumentam comissões, escalam vendas e dominam o mercado de planos de saúde para o público 49+. Leads qualificados, suporte dedicado e rentabilidade superior." />
        <link rel="canonical" href="https://medaliados.com.br/para-corretoras" />
        <meta property="og:title" content="Para Corretoras | MedAliados — Benefícios, Comissões e Diferenciais" />
        <meta property="og:description" content="Eficiência, escala e rentabilidade superior para corretoras no mercado sênior. Conheça os diferenciais exclusivos da plataforma MedAliados." />
        <meta property="og:url" content="https://medaliados.com.br/para-corretoras" />
      </Helmet>
      <PageBanner
        eyebrow="Para corretoras"
        title="O que sua corretora ganha ao operar com a MedAliados."
        sub="Aceleração comercial, rentabilidade superior e o portfólio com a maior demanda do mercado sênior."
      />

      {/* MATRIZ DE ACELERAÇÃO */}
      <Section bg="bege" className="py-24 md:py-32">
        <Reveal>
          <SectionHeader eyebrow="Matriz de aceleração comercial" align="center"
            title="Dois eixos. Um resultado: corretora pronta para escalar."
            sub="Eficiência operacional e rentabilidade financeira são tratadas como sistemas integrados, não como esforços separados."
          />
        </Reveal>

        <div className="mt-16 grid md:grid-cols-2 gap-6">
          {[
            {
              Icon: GaugeCircle, eyebrow: "Eixo 1", t: "Eficiência & Escala",
              items: [
                [LayoutGrid, "Estrutura digital completa de apoio"],
                [Users, "Geração de mais oportunidades reais de venda"],
                [Scaling, "Escala comercial sem aumento de esforço administrativo"],
                [Activity, "Acompanhamento preciso de performance"],
              ],
            },
            {
              Icon: Wallet, eyebrow: "Eixo 2", t: "Rentabilidade & Incentivos",
              items: [
                [Percent, "Comissionamento agressivo sobre vendas realizadas"],
                [Wallet, "Plataforma dedicada de acompanhamento financeiro"],
                [LifeBuoy, "Estrutura robusta de suporte comercial"],
                [Trophy, "Programa de incentivos atrelado à alta performance"],
              ],
            },
          ].map((block, bi) => (
            <Reveal key={bi} delay={bi * 120}>
              <div className="h-full rounded-2xl bg-fundo-branco border border-borda p-8">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-verde-medio">{block.eyebrow}</div>
                    <h3 className="font-display font-bold text-[1.55rem] text-verde-escuro mt-2">{block.t}</h3>
                  </div>
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-verde-escuro text-fundo-branco shrink-0">
                    <block.Icon size={22} />
                  </span>
                </div>
                <ul className="mt-7 space-y-4">
                  {block.items.map(([Icon, text], i) => (
                    <li key={i} className="flex items-start gap-4 pb-4 border-b border-borda last:border-0 last:pb-0">
                      <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-verde-escuro/5 text-verde-medio shrink-0">
                        <Icon size={16} />
                      </span>
                      <span className="text-[0.98rem] text-texto-escuro leading-relaxed pt-1.5">{text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* DIFERENCIAIS */}
      <Section bg="branco" className="py-24 md:py-32">
        <Reveal>
          <SectionHeader eyebrow="Diferenciais exclusivos" align="center"
            title="Por que corretoras escolhem a MedAliados"
            sub="Seis razões concretas para parar de operar sozinho neste mercado."
          />
        </Reveal>

        <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {[
            { Icon: MonitorSmartphone, t: "Plataforma 100% digital", d: "Acesso completo via web — sua equipe opera de qualquer lugar." },
            { Icon: Filter, t: "Leads qualificados", d: "Oportunidades já filtradas e prontas para conversão, não contatos frios." },
            { Icon: Headset, t: "Suporte comercial dedicado", d: "Um time real ao lado da sua equipe em cada etapa do funil." },
            { Icon: BarChart3, t: "Dashboard em tempo real", d: "Performance, comissão e carteira em um único painel transparente." },
            { Icon: TrendingUp, t: "Comissões acima da média", d: "Estrutura de remuneração superior à média do mercado sênior." },
            { Icon: BookOpenCheck, t: "Treinamento e onboarding", d: "Ramp-up estruturado: sua equipe domina a plataforma em até 48h." },
          ].map((d, i) => (
            <Reveal key={i} delay={(i % 3) * 80}>
              <div className="h-full rounded-xl bg-fundo-bege border border-borda p-6 hover:border-verde-medio hover:-translate-y-0.5 transition-all">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-lg bg-fundo-branco border border-borda text-verde-medio">
                  <d.Icon size={20} />
                </span>
                <h3 className="font-display font-semibold text-[1.12rem] text-verde-escuro mt-5">{d.t}</h3>
                <p className="mt-2 text-[0.94rem] text-texto-medio leading-relaxed">{d.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* DEPOIMENTOS */}
      <Section bg="bege" className="py-24 md:py-32">
        <Reveal>
          <SectionHeader eyebrow="Corretoras parceiras"
            title="O que nossas corretoras parceiras dizem"
            sub="Depoimentos ilustrativos — serão substituídos por depoimentos reais de corretoras parceiras."
          />
        </Reveal>

        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {[
            {
              q: "Desde que integramos à MedAliados, nossa operação de vendas de planos sênior cresceu 40% em 6 meses. A plataforma nos deu previsibilidade e escala que não tínhamos antes.",
              n: "Carlos Mendes", c: "Diretor · CM Corretora de Seguros", cnpj: "CNPJ 12.345.678/0001-90", metric: "+40% em 6 meses",
            },
            {
              q: "A geração de leads qualificados mudou o jogo para nós. Antes perdíamos tempo com contatos frios, agora cada oportunidade é real. O suporte comercial é excepcional.",
              n: "Fernanda Oliveira", c: "CEO · Oliveira Corretagem LTDA", cnpj: "CNPJ 23.456.789/0001-12", metric: "Pipeline 3× mais limpo",
            },
            {
              q: "O comissionamento e os incentivos da MedAliados são os melhores que já vi no mercado sênior. Nossa rentabilidade dobrou e a equipe está motivada como nunca.",
              n: "Roberto Almeida", c: "Sócio · RA Corretora Associados", cnpj: "CNPJ 34.567.890/0001-34", metric: "Rentabilidade 2×",
            },
          ].map((t, i) => (
            <Reveal key={i} delay={i * 120}>
              <div className="h-full rounded-2xl bg-fundo-branco border border-borda p-7 flex flex-col">
                <div className="flex items-start justify-between">
                  <span className="font-display text-[3rem] leading-none text-verde-lima">"</span>
                  <span className="inline-flex items-center gap-1.5 text-[11px] font-mono text-verde-medio bg-verde-escuro/5 px-2.5 py-1 rounded-full">
                    <TrendingUp size={12} />
                    {t.metric}
                  </span>
                </div>
                <p className="text-[1rem] text-texto-escuro leading-relaxed -mt-3">{t.q}</p>
                <div className="mt-auto pt-6 border-t border-borda flex items-center gap-3">
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-verde-escuro text-fundo-branco font-display font-bold shrink-0">
                    {t.n.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                  </span>
                  <div>
                    <div className="font-display font-semibold text-verde-escuro text-[0.98rem]">{t.n}</div>
                    <div className="text-[0.84rem] text-texto-medio">{t.c}</div>
                    <div className="text-[10px] font-mono text-texto-medio/75 mt-0.5">{t.cnpj}</div>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      <CTAStrip headline="Junte-se às corretoras que já estão escalando com a MedAliados." sub="Cadastro 100% B2B — exclusivo para empresas com CNPJ ativo." label="Cadastre Sua Corretora" />
    </main>
  );
}
